import { useEffect, useRef, useState } from 'react';

import type { Pun } from '../../shared/schemas';
import { sharePun, type ShareOutcome } from '../lib/share';

/** How long the outcome stays on the button before it goes back to "Share". */
const FEEDBACK_MS = 2200;

const LABEL: Record<Exclude<ShareOutcome, 'shared' | 'dismissed'>, string> = {
  copied: 'Copied!',
  failed: 'Could not copy',
};

/**
 * Share one pun.
 *
 * The label is the whole feedback mechanism. On a phone the OS sheet appears and
 * the button has nothing to say; on a desktop the pun goes to the clipboard,
 * where *nothing visible happens at all* unless the button says so.
 */
function ShareButton({ pun, authorName }: { pun: Pun; authorName?: string | null }) {
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clear on unmount: a feed re-render while the timer is pending would
  // otherwise set state on a card that is gone.
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleClick = async () => {
    const result = await sharePun(pun, authorName);

    // Nothing to report for a native share or an abandoned sheet: the OS
    // already showed the player everything that happened.
    if (result === 'shared' || result === 'dismissed') return;

    setOutcome(result);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOutcome(null), FEEDBACK_MS);
  };

  const label = outcome && outcome !== 'shared' && outcome !== 'dismissed' ? LABEL[outcome] : 'Share';

  return (
    <button
      type="button"
      onClick={handleClick}
      className="press shrink-0 rounded-full border-2 border-ink bg-white px-3 py-1 font-display text-xs font-bold shadow-pop-sm hover:bg-grape/10"
    >
      <span aria-hidden="true">&#8599;</span> {label}
    </button>
  );
}

export default ShareButton;
